import { useState, useRef, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Typography from '../../../../components/common/Typography/index'
import Card from '../../../../components/common/Card/index'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { TfiRulerAlt2 } from 'react-icons/tfi'
import { FiPlusCircle } from 'react-icons/fi'
import { IoMdArrowDropdown } from 'react-icons/io'
import { CgAttachment } from 'react-icons/cg'
import { RxLetterCaseCapitalize } from 'react-icons/rx'
import { TbArrowBackUp } from 'react-icons/tb'
import { IoCheckmarkDoneSharp } from 'react-icons/io5'
import Button from '../../../../components/common/Button'
import { ListItemAvatar, Avatar } from '@mui/material'
import MiniHeader from '../../../../components/layout/MiniHeader'
import GridTable from '../../../../components/common/GridTable/GridTable'
import { getReportDetails, sendAdminResponse, deleteReport } from '../../../../features/reports/services/reportsApi.js'

const ReportDetails = () => {
  const { reportId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { reportDetails, loading, error } = useSelector(state => state.reports)
  
  const [menuOpen, setMenuOpen] = useState(false)
  const [toastOpen, setToastOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [attachedFiles, setAttachedFiles] = useState([])
  const [textFormatting, setTextFormatting] = useState({
    bold: false,
    italic: false,
    underline: false
  })
  
  const fileInputRef = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (reportId) {
      dispatch(getReportDetails(reportId))
    }
  }, [dispatch, reportId])

  useEffect(() => {
    if (reportDetails && !subject) {
      const reportTypeLabel = getReportTypeLabel(reportDetails)
      setSubject(`Re: ${reportTypeLabel}`)
    }
  }, [reportDetails, subject])

  const getReporterInfo = (reporterId) => {
    if (!reporterId) return { name: 'Anonymous', email: 'No email provided' }
    
    return {
      name: reporterId.full_name || 
            (reporterId.firstName && reporterId.lastName 
              ? `${reporterId.firstName} ${reporterId.lastName}` 
              : reporterId.email) || 'Anonymous',
      email: reporterId.email || 'No email provided'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown time'
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} mins ago`
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hours ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getReportTypeLabel = (report) => {
    const typeLabels = {
      'MESSAGE_REPORT': 'Message Report',
      'USER_REPORT': 'User Report',
      'PROPERTY_REPORT': 'Property Report',
      'OTHER_REPORT': 'Other Report'
    }
    
    return typeLabels[report?.reportType] || report?.reportType || report?.reason || 'Report'
  }

  const handleSubmit = async () => {
    if (!message.trim()) {
      alert('Please enter a message before sending')
      return
    }

    try {
      const responseData = {
        subject: subject.trim(),
        message: message.trim(),
        isResponded: true,
      }

      await dispatch(sendAdminResponse(reportId, responseData))
      setToastOpen(false)
      setMessage('')
      setAttachedFiles([])
      alert('Response sent successfully!')
      dispatch(getReportDetails(reportId))
    } catch (error) {
      console.error('Error sending response:', error)
      alert('Failed to send response. Please try again.')
    }
  }

  const handleMarkAsResponded = async () => {
    try {
      await dispatch(sendAdminResponse(reportId, {
        subject: `Report Response: ${getReportTypeLabel(reportDetails)}`,
        message: 'This report has been reviewed and marked as responded.',
        isResponded: true,
        isMarkOnly: true
      }))
      setMenuOpen(false)
      alert('Report marked as responded!')
      dispatch(getReportDetails(reportId))
    } catch (error) {
      console.error('Error marking as responded:', error)
      alert('Failed to mark as responded. Please try again.')
    }
  }

  const handleDeleteReport = async () => {
    if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      try {
        await dispatch(deleteReport(reportId))
        alert('Report deleted successfully!')
        navigate('/reports')
      } catch (error) {
        console.error('Error deleting report:', error)
        alert('Failed to delete report. Please try again.')
      }
    }
  }

  const handleFileAttachment = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files)
    setAttachedFiles(prev => [...prev, ...files])
  }

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleTextFormatting = () => {
    const textarea = textareaRef.current
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = textarea.value.substring(start, end)
      
      if (selectedText) {
        const newText = selectedText === selectedText.toUpperCase() 
          ? selectedText.toLowerCase() 
          : selectedText.toUpperCase()
        
        const newValue = 
          textarea.value.substring(0, start) + 
          newText + 
          textarea.value.substring(end)
        
        setMessage(newValue)
        
        setTimeout(() => {
          textarea.setSelectionRange(start, start + newText.length)
          textarea.focus()
        }, 0)
      }
    }
  }

  if (loading && !reportDetails) {
    return (
      <>
        <MiniHeader title={'Reports'} />
        <div className="ml-[250px] p-8 max-w-7xl">
          <div className="flex items-center justify-center py-12">
            <Typography>Loading report details...</Typography>
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <MiniHeader title={'Reports'} />
        <div className="ml-[250px] p-8 max-w-7xl">
          <div className="flex flex-col items-center justify-center py-12">
            <Typography className="text-red-500 mb-4">Error: {error}</Typography>
            <div className="flex gap-4">
              <Button onClick={() => dispatch(getReportDetails(reportId))}>
                Retry
              </Button>
              <Button variant="secondary" onClick={() => navigate('/reports')}>
                Back to Reports
              </Button>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (!reportDetails) {
    return (
      <>
        <MiniHeader title={'Reports'} />
        <div className="ml-[250px] p-8 max-w-7xl">
          <div className="flex flex-col items-center justify-center py-12">
            <Typography className="text-gray-500 mb-4">Report not found</Typography>
            <Button onClick={() => navigate('/reports')}>
              Back to Reports
            </Button>
          </div>
        </div>
      </>
    )
  }

  const reporterInfo = getReporterInfo(reportDetails.reporterId)
  const isResponded = reportDetails.status === 'RESPONDED' || reportDetails.isResponded

  return (
    <>
      <MiniHeader title={'Reports'} />
      <div className="ml-[250px] p-8 max-w-7xl">
        <div className="bg-white px-4 py-2 border border-slate-200 rounded-md">
          <div className="flex justify-between items-start">
            <div className="flex flex-col">
              <Typography variant="h3" className="mb-2">
                {getReportTypeLabel(reportDetails)}
              </Typography>
              <Typography className="text-sm text-gray-500">
                Report ID: {reportDetails._id}
              </Typography>
            </div>
            <div className="relative">
              <button onClick={() => setMenuOpen(prev => !prev)}>
                <BsThreeDotsVertical className="text-base cursor-pointer hover:text-gray-600" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 p-2 bg-white border border-gray-200 rounded shadow-lg z-10 w-60">
                  <div
                    className="flex px-2 py-2 items-center gap-2 hover:bg-slate-100 cursor-pointer rounded"
                    onClick={() => {
                      setToastOpen(true)
                      setMenuOpen(false)
                    }}
                  >
                    <TbArrowBackUp className="text-lg" />
                    <Typography>Reply</Typography>
                  </div>
                  {!isResponded && (
                    <div 
                      className="flex px-2 py-2 items-center gap-2 hover:bg-slate-100 cursor-pointer rounded"
                      onClick={handleMarkAsResponded}
                    >
                      <IoCheckmarkDoneSharp className="text-lg" />
                      <Typography>Mark as Responded</Typography>
                    </div>
                  )}
                  <div 
                    className="flex px-2 py-2 items-center gap-2 hover:bg-slate-100 cursor-pointer rounded text-red-600"
                    onClick={handleDeleteReport}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <Typography>Delete Report</Typography>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex py-4 justify-between items-start">
            <div className="flex gap-3">
              <ListItemAvatar>
                <Avatar src={reportDetails.reporterId?.avatar || ''} />
              </ListItemAvatar>
              <div className="flex flex-col">
                <Typography variant="h4">{reporterInfo.name}</Typography>
                <Typography variant="caption" className="text-gray-600">
                  {reporterInfo.email}
                </Typography>
                <Typography variant="caption" className="text-gray-500">
                  {reportDetails.reporterModel || 'Unknown User Type'}
                </Typography>
              </div>
            </div>

            <div className="text-right">
              <Typography className="text-secondary text-sm">
                {formatDate(reportDetails.createdAt)}
              </Typography>
              <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                isResponded 
                  ? 'bg-green-100 text-green-800' 
                  : reportDetails.isRead
                    ? 'bg-blue-100 text-blue-800'
                    : reportDetails.isOpened 
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
              }`}>
                {isResponded ? 'Responded' : reportDetails.isRead ? 'Read' : reportDetails.isOpened ? 'Opened' : 'New'}
              </div>
            </div>
          </div>

          <div className="flex flex-col space-y-4">
            {reportDetails.reason && (
              <div>
                <Typography className="font-medium text-sm text-gray-700 mb-1">
                  Reason:
                </Typography>
                <Typography className="text-sm">
                  {reportDetails.reason}
                </Typography>
              </div>
            )}

            <div>
              <Typography className="font-medium text-sm text-gray-700 mb-1">
                Description:
              </Typography>
              <Typography className="py-2 text-sm leading-relaxed">
                {reportDetails.description || 'No description provided.'}
              </Typography>
            </div>

            {reportDetails.reportedEntity && (
              <div>
                <Typography className="font-medium text-sm text-gray-700 mb-1">
                  Reported Entity:
                </Typography>
                <Typography className="text-sm text-gray-600">
                  ID: {reportDetails.reportedEntity}
                </Typography>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Typography className="font-medium text-sm text-gray-700 mb-1">
                  Report Type:
                </Typography>
                <Typography className="text-sm">
                  {getReportTypeLabel(reportDetails)}
                </Typography>
              </div>
              <div>
                <Typography className="font-medium text-sm text-gray-700 mb-1">
                  Priority:
                </Typography>
                <Typography className="text-sm">
                  {reportDetails.priority || 'Normal'}
                </Typography>
              </div>
            </div>

            {reportDetails.adminResponse && (
              <div className="border-t pt-4">
                <Typography className="font-medium text-sm text-gray-700 mb-2">
                  Admin Response:
                </Typography>
                <div className="bg-gray-50 p-3 rounded">
                  {typeof reportDetails.adminResponse === 'object' ? (
                    <>
                      {reportDetails.adminResponse.subject && (
                        <Typography className="font-medium text-sm mb-2">
                          Subject: {reportDetails.adminResponse.subject}
                        </Typography>
                      )}
                      <Typography className="text-sm">
                        {reportDetails.adminResponse.message || 'No message provided'}
                      </Typography>
                      {reportDetails.adminResponse.createdAt && (
                        <Typography className="text-xs text-gray-500 mt-2">
                          Responded: {formatDate(reportDetails.adminResponse.createdAt)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography className="text-sm">
                      {reportDetails.adminResponse}
                    </Typography>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center my-4">
              <div className="flex-grow border-t-2 border-dashed border-gray-300"></div>
              <span className="px-4 text-sm text-gray-500">Actions</span>
              <div className="flex-grow border-t-2 border-dashed border-gray-300"></div>
            </div>
            
            <div className="flex gap-4 flex-wrap">
              <Button onClick={() => setToastOpen(true)}>
                {isResponded ? 'Send Another Response' : 'Respond'}
              </Button>
              {isResponded ? (
                <Button variant="secondary" disabled>
                  Already Responded
                </Button>
              ) : (
                <Button variant="secondary" onClick={handleMarkAsResponded}>
                  Mark as Responded
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/reports')}>
                Back to Reports
              </Button>
              <Button 
                variant="outline" 
                className="text-red-600 border-red-600 hover:bg-red-50"
                onClick={handleDeleteReport}
              >
                Delete Report
              </Button>
            </div>
          </div>
        </div>

        {toastOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="bg-primary flex px-4 py-3 justify-between items-center rounded-t-lg">
                <Typography variant="h4" className="text-white text-sm font-medium">
                  Respond to Report
                </Typography>
                <button 
                  className="text-white text-xl hover:bg-white hover:bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center"
                  onClick={() => setToastOpen(false)}
                >
                  &times;
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Typography className="text-sm text-gray-700 mr-2">To:</Typography>
                    <Typography className="text-sm text-primary font-medium">
                      {reporterInfo.name} ({reporterInfo.email})
                    </Typography>
                  </div>
                </div>

                <div>
                  <Typography className="text-sm text-gray-700 mb-1">Subject:</Typography>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full border border-gray-200 px-3 py-2 rounded focus:outline-none focus:border-primary text-sm"
                    placeholder="Enter subject..."
                  />
                </div>

                <div>
                  <Typography className="text-sm text-gray-700 mb-1">Message:</Typography>
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="8"
                    className="w-full border border-gray-200 px-3 py-2 rounded resize-none focus:outline-none focus:border-primary text-sm"
                    placeholder="Type your response here..."
                  />
                </div>

                {attachedFiles.length > 0 && (
                  <div className="border-t pt-3">
                    <Typography className="text-xs text-gray-600 mb-2">Attachments:</Typography>
                    <div className="space-y-1">
                      {attachedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                          <span className="text-gray-700 truncate">{file.name}</span>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 pb-6 flex justify-between items-center">
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="bg-primary text-white px-4 py-2 rounded text-sm flex items-center gap-1"
                  >
                    {loading ? 'Sending...' : 'Send Response'}
                    <IoMdArrowDropdown className="text-sm" />
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => setToastOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleTextFormatting}
                    className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                    title="Toggle text case"
                  >
                    <RxLetterCaseCapitalize className="text-lg" />
                  </button>
                  <button
                    onClick={handleFileAttachment}
                    className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                    title="Attach files"
                  >
                    <CgAttachment className="text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          className="hidden"
          accept="*/*"
        />
      </div>
    </>
  )
}

export default ReportDetails